import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://0.0.0.0:3004';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 20000, // 20s — long enough for uploads, short enough to fail fast on dead backend
});

// Global response interceptor:
//  - 401 anywhere → redirect to /login (except for /auth/user probe during bootstrap)
//  - Normalize error.message to server-provided message when available (so callers can surface it)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';

    // Don't redirect on the initial auth probe; App.js handles that via try/catch
    const isAuthProbe = url.includes('/auth/user');

    if (status === 401 && !isAuthProbe) {
      // Avoid redirect loop if we're already on /login
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }

    // Prefer server's error payload for user-facing messages
    const serverMsg = error.response?.data?.error || error.response?.data?.message;
    if (serverMsg) {
      error.userMessage = serverMsg;
    } else if (error.code === 'ECONNABORTED') {
      error.userMessage = 'Zeitüberschreitung — Server antwortet nicht.';
    } else if (!error.response) {
      error.userMessage = 'Netzwerkfehler — bitte Verbindung prüfen.';
    } else if (status >= 500) {
      error.userMessage = 'Serverfehler. Bitte später erneut versuchen.';
    } else {
      error.userMessage = error.message || 'Unbekannter Fehler';
    }

    return Promise.reject(error);
  }
);

// Helper for components: extract a user-friendly message from any error.
export const extractErrorMessage = (err, fallback = 'Aktion fehlgeschlagen') =>
  err?.userMessage || err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;

// Auth API
export const getCurrentUser = async () => {
  const response = await api.get('/auth/user');
  return response.data;
};

export const logout = async () => {
  const response = await api.post('/auth/logout');
  return response.data;
};

export const loginWithDiscord = () => {
  window.location.href = `${API_BASE_URL}/auth/discord`;
};

// Links API
export const getLinks = async (params = {}) => {
  const response = await api.get('/api/links', { params });
  return response.data;
};

export const getStats = async () => {
  const response = await api.get('/api/links/stats');
  return response.data;
};

export const deleteLink = async (id) => {
  const response = await api.delete(`/api/links/${id}`);
  return response.data;
};

export const bulkDeleteLinks = async (ids) => {
  const response = await api.post('/api/links/bulk-delete', { ids });
  return response.data;
};

export const exportLinks = async (category) => {
  const response = await api.get('/api/links/export', {
    params: { category },
    responseType: 'blob'
  });
  return response.data;
};

export const markLinkAsDownloaded = async (id, downloaded) => {
  const response = await api.patch(`/api/links/${id}/downloaded`, { downloaded });
  return response.data;
};

export const bulkMarkDownloaded = async (ids, downloaded) => {
  const response = await api.post('/api/links/bulk-downloaded', { ids, downloaded });
  return response.data;
};

// Channels API
export const getChannels = async () => {
  const response = await api.get('/api/channels');
  return response.data;
};

export const addChannel = async (channelId) => {
  const response = await api.post('/api/channels', { channelId });
  return response.data;
};

export const removeChannel = async (channelId) => {
  const response = await api.delete(`/api/channels/${channelId}`);
  return response.data;
};

// Commands API
export const getCommands = async () => {
  const response = await api.get('/api/commands');
  return response.data;
};

export const getCommand = async (commandName) => {
  const response = await api.get(`/api/commands/${commandName}`);
  return response.data;
};

export const updateCommand = async (commandName, data) => {
  const response = await api.put(`/api/commands/${commandName}`, data);
  return response.data;
};

export const toggleCommand = async (commandName) => {
  const response = await api.patch(`/api/commands/${commandName}/toggle`);
  return response.data;
};

// Count Config API
export const getCountConfig = async () => {
  const response = await api.get('/api/count-config');
  return response.data;
};

export const updateCountConfig = async (data) => {
  const response = await api.put('/api/count-config', data);
  return response.data;
};

// Custom Counters API
export const getCustomCounters = async () => {
  const response = await api.get('/api/custom-counters');
  return response.data;
};

export const getCustomCounter = async (id) => {
  const response = await api.get(`/api/custom-counters/${id}`);
  return response.data;
};

export const createCustomCounter = async (data) => {
  const response = await api.post('/api/custom-counters', data);
  return response.data;
};

export const updateCustomCounter = async (id, data) => {
  const response = await api.put(`/api/custom-counters/${id}`, data);
  return response.data;
};

export const deleteCustomCounter = async (id) => {
  const response = await api.delete(`/api/custom-counters/${id}`);
  return response.data;
};

export const incrementCounter = async (id, amount = 1) => {
  const response = await api.patch(`/api/custom-counters/${id}/increment`, { amount });
  return response.data;
};

export const decrementCounter = async (id, amount = 1) => {
  const response = await api.patch(`/api/custom-counters/${id}/decrement`, { amount });
  return response.data;
};

export const resetCounter = async (id) => {
  const response = await api.patch(`/api/custom-counters/${id}/reset`);
  return response.data;
};

export const toggleCounterEnabled = async (id) => {
  const response = await api.patch(`/api/custom-counters/${id}/toggle`);
  return response.data;
};

// Tasks API
export const getTasks = async (params = {}) => {
  const response = await api.get('/api/tasks', { params });
  return response.data;
};

export const getTask = async (id) => {
  const response = await api.get(`/api/tasks/${id}`);
  return response.data;
};

export const createTask = async (data) => {
  const response = await api.post('/api/tasks', data);
  return response.data;
};

export const updateTask = async (id, data) => {
  const response = await api.put(`/api/tasks/${id}`, data);
  return response.data;
};

export const completeTask = async (id, data = {}) => {
  const response = await api.patch(`/api/tasks/${id}/complete`, data);
  return response.data;
};

export const failTask = async (id, reason) => {
  const response = await api.patch(`/api/tasks/${id}/fail`, { reason });
  return response.data;
};

export const deleteTask = async (id) => {
  const response = await api.delete(`/api/tasks/${id}`);
  return response.data;
};

export const getTaskStats = async (userId) => {
  const response = await api.get('/api/tasks/stats/summary', { params: { user_id: userId } });
  return response.data;
};

// Points API
export const getPointsBalance = async (userId) => {
  const response = await api.get(`/api/points/balance/${userId}`);
  return response.data;
};

export const getPointsHistory = async (userId, params = {}) => {
  const response = await api.get(`/api/points/history/${userId}`, { params });
  return response.data;
};

export const addPoints = async (data) => {
  const response = await api.post('/api/points/add', data);
  return response.data;
};

export const removePoints = async (data) => {
  const response = await api.post('/api/points/remove', data);
  return response.data;
};

export const getLeaderboard = async (limit = 10) => {
  const response = await api.get('/api/points/leaderboard', { params: { limit } });
  return response.data;
};

// Checkins API
export const getCheckins = async (params = {}) => {
  const response = await api.get('/api/checkins', { params });
  return response.data;
};

export const createCheckin = async (data) => {
  const response = await api.post('/api/checkins', data);
  return response.data;
};

export const getCheckinStreak = async (userId) => {
  const response = await api.get(`/api/checkins/streak/${userId}`);
  return response.data;
};

export const getCheckinStats = async (userId) => {
  const response = await api.get(`/api/checkins/stats/${userId}`);
  return response.data;
};

// Rules API
export const getRules = async (params = {}) => {
  const response = await api.get('/api/rules', { params });
  return response.data;
};

export const createRule = async (data) => {
  const response = await api.post('/api/rules', data);
  return response.data;
};

export const updateRule = async (id, data) => {
  const response = await api.put(`/api/rules/${id}`, data);
  return response.data;
};

export const deleteRule = async (id) => {
  const response = await api.delete(`/api/rules/${id}`);
  return response.data;
};

export const logViolation = async (data) => {
  const response = await api.post('/api/rules/violations', data);
  return response.data;
};

export const getViolations = async (params = {}) => {
  const response = await api.get('/api/rules/violations', { params });
  return response.data;
};

// Rewards API
export const getRewards = async (params = {}) => {
  const response = await api.get('/api/rewards', { params });
  return response.data;
};

export const createReward = async (data) => {
  const response = await api.post('/api/rewards', data);
  return response.data;
};

export const updateReward = async (id, data) => {
  const response = await api.put(`/api/rewards/${id}`, data);
  return response.data;
};

export const deleteReward = async (id) => {
  const response = await api.delete(`/api/rewards/${id}`);
  return response.data;
};

export const redeemReward = async (data) => {
  const response = await api.post('/api/rewards/redeem', data);
  return response.data;
};

export const grantReward = async (data) => {
  const response = await api.post('/api/rewards/grant', data);
  return response.data;
};

export const getRedemptions = async (params = {}) => {
  const response = await api.get('/api/rewards/redemptions', { params });
  return response.data;
};

export const fulfillRedemption = async (id) => {
  const response = await api.patch(`/api/rewards/redemptions/${id}/fulfill`);
  return response.data;
};

// Punishments API
export const getPunishments = async (params = {}) => {
  const response = await api.get('/api/punishments', { params });
  return response.data;
};

export const createPunishment = async (data) => {
  const response = await api.post('/api/punishments', data);
  return response.data;
};

export const completePunishment = async (id, data) => {
  const response = await api.patch(`/api/punishments/${id}/complete`, data);
  return response.data;
};

export const appealPunishment = async (id, data) => {
  const response = await api.patch(`/api/punishments/${id}/appeal`, data);
  return response.data;
};

export const reviewPunishment = async (id, data) => {
  const response = await api.patch(`/api/punishments/${id}/review`, data);
  return response.data;
};

export const deletePunishment = async (id) => {
  const response = await api.delete(`/api/punishments/${id}`);
  return response.data;
};

// Inspections API
export const getInspections = async (params = {}) => {
  const response = await api.get('/api/inspections', { params });
  return response.data;
};

export const createInspection = async (data) => {
  const response = await api.post('/api/inspections', data);
  return response.data;
};

export const submitInspection = async (id, data) => {
  const response = await api.patch(`/api/inspections/${id}/submit`, data);
  return response.data;
};

export const reviewInspection = async (id, data) => {
  const response = await api.patch(`/api/inspections/${id}/review`, data);
  return response.data;
};

export const deleteInspection = async (id) => {
  const response = await api.delete(`/api/inspections/${id}`);
  return response.data;
};

// Control (Orgasm) API
export const getControlStatus = async (userId) => {
  const response = await api.get(`/api/control/status/${userId}`);
  return response.data;
};

export const controlAllow = async (data) => {
  const response = await api.post('/api/control/allow', data);
  return response.data;
};

export const controlDeny = async (data) => {
  const response = await api.post('/api/control/deny', data);
  return response.data;
};

export const controlEdge = async (data) => {
  const response = await api.post('/api/control/edge', data);
  return response.data;
};

export const controlRelease = async (data) => {
  const response = await api.post('/api/control/release', data);
  return response.data;
};
export const controlLogEdge = async (data) => {
  const response = await api.post('/api/control/log-edge', data);
  return response.data;
};
export const getControlHistory = async (params = {}) => {
  const response = await api.get('/api/control/history', { params });
  return response.data;
};
export const deleteControlHistory = async (id) => {
  const response = await api.delete(`/api/control/history/${id}`);
  return response.data;
};

// Media Tasks API
export const getMediaTasks = async (params = {}) => {
  const response = await api.get('/api/media-tasks', { params });
  return response.data;
};
export const createMediaTask = async (data) => {
  const response = await api.post('/api/media-tasks', data);
  return response.data;
};
export const updateMediaTaskStatus = async (id, data) => {
  const response = await api.patch(`/api/media-tasks/${id}/status`, data);
  return response.data;
};
export const deleteMediaTask = async (id) => {
  const response = await api.delete(`/api/media-tasks/${id}`);
  return response.data;
};

// Protocols API
export const getProtocols = async () => {
  const response = await api.get('/api/protocols');
  return response.data;
};

export const getProtocol = async (id) => {
  const response = await api.get(`/api/protocols/${id}`);
  return response.data;
};

export const createProtocol = async (data) => {
  const response = await api.post('/api/protocols', data);
  return response.data;
};

export const executeProtocol = async (id, data = {}) => {
  const response = await api.post(`/api/protocols/${id}/execute`, data);
  return response.data;
};

export const deleteProtocol = async (id) => {
  const response = await api.delete(`/api/protocols/${id}`);
  return response.data;
};

export const getProtocolLogs = async (id) => {
  const response = await api.get(`/api/protocols/${id}/logs`);
  return response.data;
};

// Characters API
export const getCharacters = async () => {
  const response = await api.get('/api/characters');
  return response.data;
};

export const getCharacter = async (id) => {
  const response = await api.get(`/api/characters/${id}`);
  return response.data;
};

export const createCharacter = async (data) => {
  const response = await api.post('/api/characters', data);
  return response.data;
};

export const updateCharacter = async (id, data) => {
  const response = await api.put(`/api/characters/${id}`, data);
  return response.data;
};

export const deleteCharacter = async (id) => {
  const response = await api.delete(`/api/characters/${id}`);
  return response.data;
};

export const createRelationship = async (data) => {
  const response = await api.post('/api/characters/relationships', data);
  return response.data;
};

export const getRelationships = async () => {
  const response = await api.get('/api/characters/relationships/all');
  return response.data;
};

// Scenes API
export const getScenes = async (params = {}) => {
  const response = await api.get('/api/scenes', { params });
  return response.data;
};

export const getScene = async (id) => {
  const response = await api.get(`/api/scenes/${id}`);
  return response.data;
};

export const createScene = async (data) => {
  const response = await api.post('/api/scenes', data);
  return response.data;
};

export const startScene = async (id) => {
  const response = await api.patch(`/api/scenes/${id}/start`);
  return response.data;
};

export const addSceneNote = async (id, data) => {
  const response = await api.patch(`/api/scenes/${id}/note`, data);
  return response.data;
};

export const endScene = async (id, data) => {
  const response = await api.patch(`/api/scenes/${id}/end`, data);
  return response.data;
};

export const archiveScene = async (id) => {
  const response = await api.patch(`/api/scenes/${id}/archive`);
  return response.data;
};

export const deleteScene = async (id) => {
  const response = await api.delete(`/api/scenes/${id}`);
  return response.data;
};

// Glamours API
export const getGlamours = async (params = {}) => {
  const response = await api.get('/api/glamours', { params });
  return response.data;
};

export const getGlamour = async (id) => {
  const response = await api.get(`/api/glamours/${id}`);
  return response.data;
};

export const createGlamour = async (data) => {
  const response = await api.post('/api/glamours', data);
  return response.data;
};

export const updateGlamour = async (id, data) => {
  const response = await api.put(`/api/glamours/${id}`, data);
  return response.data;
};

export const deleteGlamour = async (id) => {
  const response = await api.delete(`/api/glamours/${id}`);
  return response.data;
};

export const getRandomGlamour = async (params = {}) => {
  const response = await api.get('/api/glamours/random', { params });
  return response.data;
};

// Venues API
export const getVenues = async (params = {}) => {
  const response = await api.get('/api/venues', { params });
  return response.data;
};

export const getVenue = async (id) => {
  const response = await api.get(`/api/venues/${id}`);
  return response.data;
};

export const createVenue = async (data) => {
  const response = await api.post('/api/venues', data);
  return response.data;
};

export const updateVenue = async (id, data) => {
  const response = await api.put(`/api/venues/${id}`, data);
  return response.data;
};

export const toggleVenueFavorite = async (id) => {
  const response = await api.patch(`/api/venues/${id}/favorite`);
  return response.data;
};

export const visitVenue = async (id) => {
  const response = await api.patch(`/api/venues/${id}/visit`);
  return response.data;
};

export const deleteVenue = async (id) => {
  const response = await api.delete(`/api/venues/${id}`);
  return response.data;
};

// Training API
export const getTrainingPrograms = async () => {
  const response = await api.get('/api/training/programs');
  return response.data;
};

export const getTrainingProgram = async (id) => {
  const response = await api.get(`/api/training/programs/${id}`);
  return response.data;
};

export const createTrainingProgram = async (data) => {
  const response = await api.post('/api/training/programs', data);
  return response.data;
};

export const deleteTrainingProgram = async (id) => {
  const response = await api.delete(`/api/training/programs/${id}`);
  return response.data;
};

export const getTrainingEnrollments = async (params = {}) => {
  const response = await api.get('/api/training/enrollments', { params });
  return response.data;
};

export const createTrainingEnrollment = async (data) => {
  const response = await api.post('/api/training/enrollments', data);
  return response.data;
};

export const getTrainingEnrollment = async (id) => {
  const response = await api.get(`/api/training/enrollments/${id}`);
  return response.data;
};

export const completeTrainingDay = async (id, data = {}) => {
  const response = await api.patch(`/api/training/enrollments/${id}/complete`, data);
  return response.data;
};

export const skipTrainingDay = async (id, data = {}) => {
  const response = await api.patch(`/api/training/enrollments/${id}/skip`, data);
  return response.data;
};

// Journals API
export const getJournals = async (params = {}) => {
  const response = await api.get('/api/journals', { params });
  return response.data;
};

export const getJournal = async (id) => {
  const response = await api.get(`/api/journals/${id}`);
  return response.data;
};

export const createJournal = async (data) => {
  const response = await api.post('/api/journals', data);
  return response.data;
};

export const updateJournal = async (id, data) => {
  const response = await api.put(`/api/journals/${id}`, data);
  return response.data;
};

export const deleteJournal = async (id) => {
  const response = await api.delete(`/api/journals/${id}`);
  return response.data;
};

export const searchJournals = async (query) => {
  const response = await api.get('/api/journals/search', { params: { q: query } });
  return response.data;
};

// Contracts API
export const getContracts = async (params = {}) => {
  const response = await api.get('/api/contracts', { params });
  return response.data;
};

export const getContract = async (id) => {
  const response = await api.get(`/api/contracts/${id}`);
  return response.data;
};

export const createContract = async (data) => {
  const response = await api.post('/api/contracts', data);
  return response.data;
};

export const signContract = async (id) => {
  const response = await api.patch(`/api/contracts/${id}/sign`);
  return response.data;
};

export const terminateContract = async (id) => {
  const response = await api.patch(`/api/contracts/${id}/terminate`);
  return response.data;
};

export const updateContract = async (id, data) => {
  const response = await api.put(`/api/contracts/${id}`, data);
  return response.data;
};

// Activity Log API
export const getActivityLog = async (params = {}) => {
  const response = await api.get('/api/activity-log', { params });
  return response.data;
};

export const searchActivityLog = async (query) => {
  const response = await api.get('/api/activity-log/search', { params: { q: query } });
  return response.data;
};

// User Settings API
export const getUserSettings = async () => {
  const response = await api.get('/api/user-settings');
  return response.data;
};

export const updateUserSettings = async (data) => {
  const response = await api.put('/api/user-settings', data);
  return response.data;
};

// Guild Members API
export const getGuildMembers = async () => {
  const response = await api.get('/api/users/guild-members');
  return response.data;
};

// Morning Reports API
export const getMorningReports = async (params = {}) => {
  const response = await api.get('/api/morning', { params });
  return response.data;
};
export const createMorningReport = async (data) => {
  const response = await api.post('/api/morning', data);
  return response.data;
};

// Evening Reports API
export const getEveningReports = async (params = {}) => {
  const response = await api.get('/api/evening', { params });
  return response.data;
};
export const createEveningReport = async (data) => {
  const response = await api.post('/api/evening', data);
  return response.data;
};

// Speech Rules API
export const getSpeechRules = async () => {
  const response = await api.get('/api/speech');
  return response.data;
};
export const createSpeechRule = async (data) => {
  const response = await api.post('/api/speech', data);
  return response.data;
};
export const deleteSpeechRule = async (id) => {
  const response = await api.delete(`/api/speech/${id}`);
  return response.data;
};
export const getSpeechViolations = async (params = {}) => {
  const response = await api.get('/api/speech/violations', { params });
  return response.data;
};
export const reportSpeechViolation = async (data) => {
  const response = await api.post('/api/speech/violations', data);
  return response.data;
};

// Clothing Rules API
export const getClothingRules = async (params = {}) => {
  const response = await api.get('/api/clothing', { params });
  return response.data;
};
export const createClothingRule = async (data) => {
  const response = await api.post('/api/clothing', data);
  return response.data;
};
export const updateClothingRule = async (id, data) => {
  const response = await api.put(`/api/clothing/${id}`, data);
  return response.data;
};
export const deleteClothingRule = async (id) => {
  const response = await api.delete(`/api/clothing/${id}`);
  return response.data;
};

// Grooming API
export const getGroomingSchedule = async (params = {}) => {
  const response = await api.get('/api/grooming', { params });
  return response.data;
};
export const createGroomingTask = async (data) => {
  const response = await api.post('/api/grooming', data);
  return response.data;
};
export const getGroomingLogs = async (params = {}) => {
  const response = await api.get('/api/grooming/logs', { params });
  return response.data;
};
export const logGrooming = async (data) => {
  const response = await api.post('/api/grooming/logs', data);
  return response.data;
};

// Sleep API
export const getSleepConfig = async (params = {}) => {
  const response = await api.get('/api/sleep/config', { params });
  return response.data;
};
export const updateSleepConfig = async (data) => {
  const response = await api.post('/api/sleep/config', data);
  return response.data;
};
export const getSleepLogs = async (params = {}) => {
  const response = await api.get('/api/sleep/logs', { params });
  return response.data;
};
export const createSleepLog = async (data) => {
  const response = await api.post('/api/sleep/logs', data);
  return response.data;
};

// Food API
export const getFoodRules = async (params = {}) => {
  const response = await api.get('/api/food', { params });
  return response.data;
};
export const createFoodRule = async (data) => {
  const response = await api.post('/api/food', data);
  return response.data;
};
export const deleteFoodRule = async (id) => {
  const response = await api.delete(`/api/food/${id}`);
  return response.data;
};
export const getFoodLogs = async (params = {}) => {
  const response = await api.get('/api/food/logs', { params });
  return response.data;
};
export const createFoodLog = async (data) => {
  const response = await api.post('/api/food/logs', data);
  return response.data;
};

// Orgasm Control API
export const getOrgasmStatus = async (userId) => {
  const response = await api.get(`/api/orgasm/status/${userId}`);
  return response.data;
};
export const getOrgasmRequests = async (params = {}) => {
  const response = await api.get('/api/orgasm/requests', { params });
  return response.data;
};
export const createOrgasmRequest = async (data) => {
  const response = await api.post('/api/orgasm/requests', data);
  return response.data;
};
export const respondOrgasmRequest = async (id, data) => {
  const response = await api.patch(`/api/orgasm/requests/${id}`, data);
  return response.data;
};
export const getOrgasmLogs = async (params = {}) => {
  const response = await api.get('/api/orgasm/logs', { params });
  return response.data;
};
export const createOrgasmLog = async (data) => {
  const response = await api.post('/api/orgasm/logs', data);
  return response.data;
};
export const deleteOrgasmLog = async (id) => {
  const response = await api.delete(`/api/orgasm/logs/${id}`);
  return response.data;
};
export const deleteOrgasmRequest = async (id) => {
  const response = await api.delete(`/api/orgasm/requests/${id}`);
  return response.data;
};

// Week Planning API
export const getWeekPlans = async (params = {}) => {
  const response = await api.get('/api/week', { params });
  return response.data;
};
export const createWeekPlan = async (data) => {
  const response = await api.post('/api/week', data);
  return response.data;
};
export const getWeekReviews = async (params = {}) => {
  const response = await api.get('/api/week/reviews', { params });
  return response.data;
};

// Month Planning API
export const getMonthGoals = async (params = {}) => {
  const response = await api.get('/api/month/goals', { params });
  return response.data;
};
export const createMonthGoal = async (data) => {
  const response = await api.post('/api/month/goals', data);
  return response.data;
};
export const getMonthReviews = async (params = {}) => {
  const response = await api.get('/api/month/reviews', { params });
  return response.data;
};
export const getMonthCeremonies = async (params = {}) => {
  const response = await api.get('/api/month/ceremonies', { params });
  return response.data;
};
export const createMonthCeremony = async (data) => {
  const response = await api.post('/api/month/ceremonies', data);
  return response.data;
};

// Safety System API
export const getSafeSignals = async (params = {}) => {
  const response = await api.get('/api/safe/signals', { params });
  return response.data;
};
export const createSafeSignal = async (data) => {
  const response = await api.post('/api/safe/signals', data);
  return response.data;
};
export const getSafeChecks = async (params = {}) => {
  const response = await api.get('/api/safe/checks', { params });
  return response.data;
};
export const createSafeCheck = async (data) => {
  const response = await api.post('/api/safe/checks', data);
  return response.data;
};

// Activity Sessions API
export const getActivitySessions = async (params = {}) => {
  const response = await api.get('/api/activity-sessions', { params });
  return response.data;
};
export const createActivitySession = async (data) => {
  const response = await api.post('/api/activity-sessions', data);
  return response.data;
};
export const endActivitySession = async (id, data) => {
  const response = await api.patch(`/api/activity-sessions/${id}/end`, data);
  return response.data;
};
export const abortActivitySession = async (id, data) => {
  const response = await api.patch(`/api/activity-sessions/${id}/abort`, data);
  return response.data;
};
export const getActivityLogs = async (params = {}) => {
  const response = await api.get('/api/activity-sessions/logs', { params });
  return response.data;
};
export const createActivityLog = async (data) => {
  const response = await api.post('/api/activity-sessions/logs', data);
  return response.data;
};

// Toys API
export const getToys = async () => {
  const response = await api.get('/api/toys');
  return response.data;
};
export const createToy = async (data) => {
  const response = await api.post('/api/toys', data);
  return response.data;
};
export const updateToy = async (id, data) => {
  const response = await api.put(`/api/toys/${id}`, data);
  return response.data;
};
export const deleteToy = async (id) => {
  const response = await api.delete(`/api/toys/${id}`);
  return response.data;
};
export const getToySessionsLog = async (params = {}) => {
  const response = await api.get('/api/toys/sessions', { params });
  return response.data;
};
export const createToySession = async (data) => {
  const response = await api.post('/api/toys/sessions', data);
  return response.data;
};

// Guided Sessions API
export const getGuidedSessions = async (params = {}) => {
  const response = await api.get('/api/sessions', { params });
  return response.data;
};
export const createGuidedSession = async (data) => {
  const response = await api.post('/api/sessions', data);
  return response.data;
};
export const endGuidedSession = async (id, data) => {
  const response = await api.patch(`/api/sessions/${id}/end`, data);
  return response.data;
};
export const getSessionSteps = async (sessionId) => {
  const response = await api.get(`/api/sessions/${sessionId}/steps`);
  return response.data;
};
export const addSessionStep = async (sessionId, data) => {
  const response = await api.post(`/api/sessions/${sessionId}/steps`, data);
  return response.data;
};
export const completeSessionStep = async (sessionId, stepId, data) => {
  const response = await api.patch(`/api/sessions/${sessionId}/steps/${stepId}/complete`, data);
  return response.data;
};

// Challenges API
export const getChallenges = async (params = {}) => {
  const response = await api.get('/api/challenges', { params });
  return response.data;
};
export const createChallenge = async (data) => {
  const response = await api.post('/api/challenges', data);
  return response.data;
};
export const getChallenge = async (id) => {
  const response = await api.get(`/api/challenges/${id}`);
  return response.data;
};
export const logChallengeProgress = async (id, data) => {
  const response = await api.post(`/api/challenges/${id}/progress`, data);
  return response.data;
};
export const completeChallenge = async (id) => {
  const response = await api.patch(`/api/challenges/${id}/complete`);
  return response.data;
};

// Routines API
export const getRoutines = async (params = {}) => {
  const response = await api.get('/api/routines', { params });
  return response.data;
};
export const createRoutine = async (data) => {
  const response = await api.post('/api/routines', data);
  return response.data;
};
export const updateRoutine = async (id, data) => {
  const response = await api.put(`/api/routines/${id}`, data);
  return response.data;
};
export const deleteRoutine = async (id) => {
  const response = await api.delete(`/api/routines/${id}`);
  return response.data;
};
export const getRoutineLogs = async (params = {}) => {
  const response = await api.get('/api/routines/logs', { params });
  return response.data;
};
export const logRoutine = async (data) => {
  const response = await api.post('/api/routines/logs', data);
  return response.data;
};

// Aftercare API
export const getAftercareRecords = async (params = {}) => {
  const response = await api.get('/api/aftercare', { params });
  return response.data;
};
export const createAftercareRecord = async (data) => {
  const response = await api.post('/api/aftercare', data);
  return response.data;
};

// Limits API
export const getLimits = async (params = {}) => {
  const response = await api.get('/api/limits', { params });
  return response.data;
};
export const createLimit = async (data) => {
  const response = await api.post('/api/limits', data);
  return response.data;
};
export const updateLimit = async (id, data) => {
  const response = await api.put(`/api/limits/${id}`, data);
  return response.data;
};
export const deleteLimit = async (id) => {
  const response = await api.delete(`/api/limits/${id}`);
  return response.data;
};

export default api;
